import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import RoleSwitcher from "./RoleSwitcher";

/**
 * AdminShell — mobile-first responsive layout for both the tenant-admin and
 * merchant back-offices.
 *
 *  - ≥1024px (lg): fixed left sidebar + scrollable main area.
 *  - <1024px: top app bar with hamburger; sidebar slides in from the left
 *    as an off-canvas drawer (with a backdrop). Auto-closes on route change.
 *  - The main area is `overflow-x-auto` so wide tables (Orders, Inventory,
 *    Reports) scroll horizontally on small screens without breaking layout.
 */
export default function AdminShell({
    items,
    user,
    title,
    subtitle,
    onLogout,
    sidebarExtra = null,
    children,
    testidPrefix = "nav",
}) {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    // Close drawer whenever the user navigates somewhere.
    useEffect(() => { setOpen(false); }, [pathname]);

    // Lock body scroll while drawer is open
    useEffect(() => {
        if (open) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    const sidebar = (
        <>
            <div className="px-5 py-5 border-b border-border">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{title}</p>
                <h2 className="font-bold text-lg mt-0.5 truncate">{user?.first_name || subtitle || "User"}</h2>
                {user?.mobile_number && <p className="text-xs text-muted-foreground mt-0.5 truncate">+91 {user.mobile_number}</p>}
            </div>
            <nav className="px-3 py-4 space-y-1 flex-1 overflow-y-auto">
                {items.map(({ to, icon: Icon, label, end }) => (
                    <NavLink
                        key={to}
                        to={to}
                        end={end}
                        data-testid={`${testidPrefix}-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                    >
                        <Icon size={18} />{label}
                    </NavLink>
                ))}
            </nav>
            {sidebarExtra}
            <div className="px-3 mt-1 mb-2">
                <RoleSwitcher
                    className="w-full [&>button]:w-full [&>button]:justify-between"
                    dropDirection="up"
                />
            </div>
            <button onClick={onLogout} className="mx-3 mb-4 mt-2 flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-destructive">
                <LogOut size={18} /> Logout
            </button>
        </>
    );

    return (
        <div className="font-admin min-h-screen bg-muted/40 lg:grid lg:grid-cols-[260px_1fr]">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex flex-col bg-card border-r border-border min-h-screen sticky top-0 max-h-screen">
                {sidebar}
            </aside>

            {/* Mobile top app bar */}
            <header className="lg:hidden sticky top-0 z-30 bg-card border-b border-border flex items-center gap-3 px-3 py-3">
                <button
                    data-testid={`${testidPrefix}-menu-open`}
                    onClick={() => setOpen(true)}
                    className="p-2 rounded-lg hover:bg-muted"
                    aria-label="Open menu"
                >
                    <Menu size={20} />
                </button>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{title}</p>
                    <p className="font-bold text-sm truncate">{user?.first_name || subtitle || "User"}</p>
                </div>
                <RoleSwitcher />
            </header>

            {/* Mobile drawer + backdrop */}
            {open && (
                <>
                    <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setOpen(false)} />
                    <aside
                        data-testid={`${testidPrefix}-drawer`}
                        className="lg:hidden fixed inset-y-0 left-0 z-50 w-[78%] max-w-[300px] bg-card border-r border-border flex flex-col shadow-2xl animate-in slide-in-from-left duration-200"
                    >
                        <button
                            data-testid={`${testidPrefix}-menu-close`}
                            onClick={() => setOpen(false)}
                            className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-muted"
                            aria-label="Close menu"
                        >
                            <X size={18} />
                        </button>
                        {sidebar}
                    </aside>
                </>
            )}

            {/* Main content: overflow-x-auto lets wide tables scroll without breaking layout */}
            <main className="p-3 sm:p-4 lg:p-8 max-w-full lg:max-w-7xl overflow-x-auto">
                {children}
            </main>
        </div>
    );
}
